import { Alert, Descriptions, Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatDuration } from '@/shared/lib/format'
import { DeviceGatewayIndex } from './DeviceGatewayIndex'
import { TechnicalDetail } from './TechnicalDetail'
import {
  CLOCK_SKEW_WARN_SECONDS,
  type AttendanceDevice,
  type DeviceProbeResult,
} from './attendanceApi'

interface DeviceProbeModalProps {
  /** `null` — natija hali yo'q, oyna yopiq */
  result: DeviceProbeResult | null
  /**
   * Tekshirilgan qurilma yozuvi — `null` bo'lsa oyna yopiq.
   *
   * ⚠️ Ilgari bu yerda faqat NOM (`deviceName`) turardi. Endi butun qator
   * kerak: gateway rejimidagi qurilmaning `gatewayDevIndex` i probe
   * JAVOBIDA yo'q — u qurilma yozuvida turadi va aynan shu oynada eng
   * kerak bo'ladi ("gateway uni topdimi?" degan savol "nega ulanmadi"
   * savolining birinchi ayrimchisi).
   */
  device: AttendanceDevice | null
  onClose: () => void
}

/**
 * "Ulanishni tekshirish" natijasi.
 *
 * <h2>⚠️ NATIJA MODALDA, JADVAL KATAGIDA EMAS</h2>
 * Javob bir jumlali "tirik/o'lik" emas. Muvaffaqiyatda uchta diagnostik
 * qiymat qaytadi (model, seriya, proshivka) va ular "qaysi proshivkada
 * ishlamadi" degan savolga javob beradi; muvaffaqiyatsizlikda esa SABAB
 * kerak, va u tor katakka sig'maydi. Qisqartirilgan "javob yo'q" degan
 * yozuv operatorni qurilma o'chiq deb o'ylashga majbur qilardi — holbuki
 * parol mos kelmagan bo'lishi mumkin (`NasPage` dagi bilan bir xil
 * mulohaza va bir xil yechim).
 *
 * <h2>⚠️ XATO SABABI OCHIQ MATN BILAN</h2>
 * "Xato yuz berdi" degan umumiy so'z bu yerda taqiqlangan: u operatorga
 * nima qilish kerakligini AYTMAYDI. Server sababni o'zi tayyorlab
 * yuboradi (`detail`) — qurilmaning javobini u ko'rgan, biz ko'rmaganmiz.
 * Shuning uchun bu matn qayta yozilmaydi va yashirilmaydi.
 */
export function DeviceProbeModal({ result, device, onClose }: DeviceProbeModalProps) {
  const { t } = useTranslation()

  const alertType =
    result === null
      ? 'info'
      : result.status === 'ONLINE'
        ? 'success'
        : // ⚠️ `AUTH_FAILED` — QIZIL, `UNREACHABLE` — SARIQ.
          // Birinchisi odam aralashuvini TALAB QILADI (parol mos emas va
          // u o'zi tuzalmaydi), ikkinchisi esa vaqtinchalik bo'lishi
          // mumkin. Ranglar `DeviceStatusTag` bilan AYNAN bir xil —
          // bir xil holat ikki joyda ikki xil rangda ko'rinsa, operator
          // ularni boshqa-boshqa narsa deb o'ylardi.
          result.status === 'AUTH_FAILED'
          ? 'error'
          : result.status === 'UNREACHABLE'
            ? 'warning'
            : 'info'

  const skew = result?.clockSkewSeconds ?? null
  // `>=` — backend `hasSignificantClockSkew()` bilan AYNAN bir xil
  // solishtiruv (`DeviceClockSkew` dagi izoh).
  const skewWarn = skew !== null && Math.abs(skew) >= CLOCK_SKEW_WARN_SECONDS
  const info = result?.deviceInfo ?? null

  return (
    <Modal
      open={result !== null}
      title={t('attendance.probeTitle', { name: device?.name ?? '' })}
      onCancel={onClose}
      onOk={onClose}
      okText={t('common.close')}
      cancelButtonProps={{ style: { display: 'none' } }}
      destroyOnHidden
      width={560}
    >
      {result ? (
        <>
          {/*
            ⚠️ TARTIB 2026-08-16 DA ALMASHTIRILDI (BUG-033).

            Ilgari bu yerda `description={result.detail ?? ...}` turardi,
            ya'ni serverning XOM matni tarjima qilingan tushuntirishni
            BUTUNLAY siqib chiqarardi — u faqat `detail` bo'lmaganda
            ko'rinardi. Amalda esa `detail` deyarli DOIM bo'ladi
            (`DeviceHealthServiceImpl.describe` uni har nosozlikda
            yozadi), ya'ni tarjima qilingan matn amalda O'LIK edi va
            operator `java.net.SocketTimeoutException` ni o'qirdi.

            Endi tushuntirish DOIM ko'rinadi, texnik qator esa yopiq
            holatda ostida turadi (`TechnicalDetail` javadoc'i).
          */}
          <Alert
            type={alertType}
            showIcon
            message={t(`attendance.status.${result.status}`)}
            description={
              <>
                {t(`attendance.statusHint.${result.status}`)}
                <TechnicalDetail text={result.detail} />
              </>
            }
          />

          {/*
            ⚠️ GATEWAY BO'LIMI — FAQAT `ISUP_GATEWAY` da va HOLATDAN
            QAT'I NAZAR (qurilma ma'lumotidan farqli, u faqat `ONLINE`
            da chiziladi). Sabab teskari: bu yerda eng kerakli holat
            aynan MUVAFFAQIYATSIZ probe. «Yetib bo'lmadi» xabarini
            ko'rgan operatorning birinchi savoli — «gateway bu qurilmani
            umuman ko'rganmi?». Javob shu qatorda: o'rin topilgan bo'lsa
            muammo qurilmada, topilmagan bo'lsa gateway ro'yxatida yoki
            kiritilgan identifikatorda.

            ⚠️ `DIRECT_ISAPI` da BU BLOK UMUMAN YO'Q — o'sha rejimda
            gateway tushunchasi mavjud emas va bo'sh qatorlar oynani
            «to'ldirilmagan» qilib ko'rsatardi.
          */}
          {device !== null && device.connectionType === 'ISUP_GATEWAY' ? (
            <Descriptions column={1} size="small" bordered style={{ marginTop: 16 }}>
              <Descriptions.Item label={t('attendance.isupDeviceId')}>
                {device.isupDeviceId === null ? (
                  t('common.dash')
                ) : (
                  <Typography.Text code copyable={{ text: device.isupDeviceId }}>
                    {device.isupDeviceId}
                  </Typography.Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('attendance.gatewayDevIndex')}>
                <DeviceGatewayIndex devIndex={device.gatewayDevIndex} />
              </Descriptions.Item>
            </Descriptions>
          ) : null}

          {/*
            ⚠️ Qurilma ma'lumoti FAQAT ulanish o'tganda ko'rsatiladi.
            Muvaffaqiyatsiz probe'da bu maydonlar bo'sh keladi va ularni
            "—" bilan chizish "qurilma javob berdi, lekin o'zi haqida
            aytmadi" degan yolg'on taassurot berardi.
          */}
          {result.status === 'ONLINE' ? (
            <Descriptions column={1} size="small" bordered style={{ marginTop: 16 }}>
              <Descriptions.Item label={t('attendance.probeModel')}>
                {info?.model ?? t('common.dash')}
              </Descriptions.Item>
              <Descriptions.Item label={t('attendance.probeSerial')}>
                {info?.serialNumber ? (
                  <Typography.Text code copyable={{ text: info.serialNumber }}>
                    {info.serialNumber}
                  </Typography.Text>
                ) : (
                  t('common.dash')
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('attendance.probeFirmware')}>
                {info?.firmwareVersion ?? t('common.dash')}
              </Descriptions.Item>

              {/*
                ⚠️ QURILMA SOATI XOM HOLDA ko'rsatiladi — tenant zonasiga
                O'GIRILMAYDI. Butun ustunning ma'nosi shunda: qurilma
                `2026-08-11T16:39:32+08:00` deb yozadi, biz esa uni
                "to'g'rilab" ko'rsatsak, aynan tekshirilayotgan nosozlik
                KO'RINMAY qolardi. Panel bo'ylab vaqtlar tenant zonasida
                ko'rsatiladi (`shared/lib/format.ts`), va bu — o'sha
                qoidadan ATAYLAB qilingan yagona istisno.
              */}
              <Descriptions.Item label={t('attendance.probeDeviceTime')}>
                {result.deviceLocalTime ? (
                  <Typography.Text code>{result.deviceLocalTime}</Typography.Text>
                ) : (
                  t('common.dash')
                )}
              </Descriptions.Item>

              {/*
                ⚠️ Qurilma E'LON QILGAN zona satri — xom holda va ALOHIDA
                qator. Aynan shu maydon 2026-08-11 dagi hodisaning isboti
                bo'lgan: qurilma Toshkent vaqtini ko'rsatib turib uni
                `+08:00` deb belgilagan. Uni yashirish yoki "chiroyli"
                nomga o'girish o'sha isbotni yo'q qilardi.
              */}
              <Descriptions.Item label={t('attendance.probeDeviceZone')}>
                {result.deviceTimeZoneRaw ? (
                  <Typography.Text code>{result.deviceTimeZoneRaw}</Typography.Text>
                ) : (
                  t('common.dash')
                )}
              </Descriptions.Item>

              <Descriptions.Item label={t('attendance.probeSkew')}>
                {skew === null ? (
                  t('common.dash')
                ) : skewWarn ? (
                  <Typography.Text type="warning">
                    {skew > 0 ? '+' : '−'}
                    {formatDuration(Math.abs(skew))}
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary">
                    {Math.abs(skew) === 0
                      ? t('attendance.clockSkewNone')
                      : `${skew > 0 ? '+' : '−'}${formatDuration(Math.abs(skew))}`}
                  </Typography.Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          ) : null}

          {/*
            ⚠️ Soat farqi ogohlantirishi ALOHIDA blok, jadvaldagi kichik
            belgidan tashqari. Sabab: ulanish MUVAFFAQIYATLI bo'lganda
            operator "hammasi joyida" deb oynani yopadi — holbuki aynan
            shu holatda eng jimgina nosozlik yashiringan bo'lishi mumkin
            (`DeviceClockSkew` sarlavhasidagi 2026-08-11 hodisasi).
          */}
          {skewWarn ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              message={t('attendance.clockSkewWarnTitle')}
              description={t('attendance.clockSkewWarnHint')}
            />
          ) : null}
        </>
      ) : null}
    </Modal>
  )
}
